import { useState } from 'react';
import { adminQueryCount, adminQueryGet } from '@/client/admin.client';
import type {
  AdminQueryCountInput,
  AdminQueryGetInput,
  AdminQueryGetResult,
} from '@rb/shared/admin-query.types';
import {
  listSavedQueries,
  saveQuery,
  deleteSavedQuery,
  type SavedQuery,
} from '@/lib/saved-queries';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const PLACEHOLDER = `{
  "collection": "users",
  "filters": [
    { "field": "isGuest", "operator": "==", "value": true },
    { "field": "lastClearedStageId", "operator": ">=", "value": 10 }
  ],
  "orderBy": { "field": "createdAt", "direction": "desc" },
  "limit": 50
}`;

/** 셀 값을 표시용 문자열로 변환한다. 객체·배열은 JSON.stringify. */
function formatCell(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

/** records 에서 발견되는 모든 컬럼 키 목록을 반환한다. __id 는 앞으로 이동. */
function discoverColumns(records: AdminQueryGetResult['records']): string[] {
  const seen = new Set<string>();
  for (const row of records) {
    for (const key of Object.keys(row)) {
      seen.add(key);
    }
  }
  const cols = Array.from(seen).filter((k) => k !== '__id');
  return ['__id', ...cols];
}

export function QueryConsolePanel() {
  const [text, setText] = useState(PLACEHOLDER);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rpcError, setRpcError] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [rows, setRows] = useState<AdminQueryGetResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedName, setSavedName] = useState('');
  const [saved, setSaved] = useState<SavedQuery[]>(() => listSavedQueries());

  /** JSON을 파싱하고 collection 필드를 검증. 실패 시 parseError 세팅 후 null 반환. */
  const parseQuery = (): Record<string, unknown> | null => {
    try {
      const obj = JSON.parse(text) as Record<string, unknown>;
      if (!obj || typeof obj !== 'object' || typeof obj.collection !== 'string') {
        setParseError('collection(string) 필드가 필요합니다.');
        return null;
      }
      setParseError(null);
      return obj;
    } catch (e) {
      setParseError(`JSON 파싱 오류: ${(e as Error).message}`);
      return null;
    }
  };

  const handleCount = async () => {
    const q = parseQuery();
    if (!q) return;
    setLoading(true);
    setRpcError(null);
    setRows(null);
    try {
      const n = await adminQueryCount(q as unknown as AdminQueryCountInput);
      setCount(n);
    } catch (e) {
      setRpcError((e as Error).message);
      setCount(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGet = async (cursor?: string | number | null) => {
    const q = parseQuery();
    if (!q) return;
    setLoading(true);
    setRpcError(null);
    setCount(null);
    try {
      const payload =
        cursor != null
          ? { ...(q as unknown as AdminQueryGetInput), cursor }
          : (q as unknown as AdminQueryGetInput);
      const res = await adminQueryGet(payload);
      setRows((prev) =>
        cursor != null && prev != null
          ? { ...res, records: [...prev.records, ...res.records] }
          : res
      );
    } catch (e) {
      setRpcError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    const name = savedName.trim();
    if (!name) return;
    setSaved(saveQuery(name, text));
    setSavedName('');
  };

  const handleDelete = (name: string) => {
    setSaved(deleteSavedQuery(name));
  };

  const columns = rows ? discoverColumns(rows.records) : [];

  return (
    <div className="flex flex-col gap-4">
      {/* 쿼리 에디터 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">쿼리 JSON</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setParseError(null);
            }}
            className="min-h-48 font-mono text-xs"
            spellCheck={false}
          />

          {parseError && (
            <Alert variant="destructive">
              <AlertDescription>
                <pre className="whitespace-pre-wrap break-all text-xs">{parseError}</pre>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={handleCount} disabled={loading}>
              Count
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => handleGet()}
              disabled={loading}
            >
              Get rows
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* RPC 에러 */}
      {rpcError && (
        <Alert variant="destructive">
          <AlertDescription>
            <pre className="whitespace-pre-wrap break-all text-xs">{rpcError}</pre>
          </AlertDescription>
        </Alert>
      )}

      {/* Count 결과 */}
      {count !== null && (
        <Card>
          <CardContent className="p-4">
            <span className="text-sm font-medium text-muted-foreground">Count: </span>
            <span className="font-mono text-lg font-semibold">{count.toLocaleString()}</span>
          </CardContent>
        </Card>
      )}

      {/* Get 결과 */}
      {rows !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              결과 — {rows.records.length.toLocaleString()}건
              {rows.hasMore && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">(더 있음)</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {rows.records.length === 0 ? (
              <p className="text-sm text-muted-foreground">결과 없음</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((col) => (
                        <TableHead key={col} className="font-mono text-xs">
                          {col}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.records.map((row) => (
                      <TableRow key={row.__id}>
                        {columns.map((col) => (
                          <TableCell key={col} className="max-w-xs truncate font-mono text-xs">
                            {formatCell(row[col])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {rows.hasMore && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleGet(rows.nextCursor)}
                disabled={loading}
              >
                {loading ? '불러오는 중…' : 'Load more'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* 저장된 쿼리 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">저장된 쿼리</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {/* 저장 입력 */}
          <div className="flex gap-2">
            <Input
              value={savedName}
              onChange={(e) => setSavedName(e.target.value)}
              placeholder="쿼리 이름"
              className="max-w-xs text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleSave}
              disabled={!savedName.trim()}
            >
              저장
            </Button>
          </div>

          {/* 저장 목록 */}
          {saved.length === 0 ? (
            <p className="text-xs text-muted-foreground">저장된 쿼리 없음</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {saved.map((q) => (
                <li key={q.name} className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-auto justify-start px-2 py-1 text-left font-mono text-xs"
                    onClick={() => {
                      setText(q.body);
                      setParseError(null);
                    }}
                  >
                    {q.name}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-auto px-1 py-1 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => handleDelete(q.name)}
                  >
                    ✕
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
